import * as React from 'react';
import {createViewConfig, view, toIntent} from '@sail/engine';
import type {View} from '@sail/engine';
import {useId, useControlledState} from '@sail/react-aria';
import type {IconAsset} from '@sail/icons/types';
import {tokens as $} from '@sail/tokens';

import {Label} from './Label';
import {Icon, IconConfig} from './Icon';
import {LinkConfig} from './Link';
import {ActionConfig} from './Action';
import type {FormFieldGroupProps} from './FormFieldGroup';
import {FormFieldGroup, FormFieldGroupConfig} from './FormFieldGroup';
import type {FormFieldProps} from './FormField';
import {NativeCheckbox} from './Checkbox';
import {NativeRadio} from './Radio';
import {useFormFieldProps} from './FormField';
import {css} from '../css';

/* -------- Helpers --------*/

const horizontalPadding = {
  small: css({paddingX: 'small'}),
  medium: css({paddingX: 'space.150'}),
  large: css({paddingX: 'medium'}),
};

const verticalPadding = {
  small: css({paddingY: 'xsmall'}),
  medium: css({paddingY: 'small'}),
  large: css({paddingY: 'small'}),
};

const bleedTop = {
  small: css({bleedTop: 'xsmall'}),
  medium: css({bleedTop: 'small'}),
  large: css({bleedTop: 'small'}),
};

const bleedX = {
  small: css({bleedX: 'small'}),
  medium: css({bleedX: 'space.150'}),
  large: css({bleedX: 'medium'}),
};

const gap = {
  small: css({gap: 'space.75'}),
  medium: css({gap: 'small'}),
  large: css({gap: 'space.150'}),
};

const actionOverrides = toIntent([
  ActionConfig.customize({
    css: {
      // This ensures actions are clickable and sit above the clickable layer formed by the radio input
      position: 'relative',
    },
  }),
  LinkConfig.customize({
    defaults: {type: 'secondary'},
    css: {color: 'subdued'},
  }),
]);

/* -------- Context --------*/

export const ToggleContext = React.createContext<{
  selectedValue?: string;
  onSelectionChange?: (value: string) => void;
  selectionMode: 'single' | 'multiple';
  selectedValues?: string[];
  onMultiSelectionChange?: (values: string[]) => void;
  size: 'small' | 'medium' | 'large';
  layout: FormFieldGroupProps['layout'];
  id?: string;
  name?: string;
  disabled: boolean;
  showInput: boolean;
}>({
  size: 'medium',
  disabled: false,
  layout: 'horizontal',
  selectionMode: 'single',
  showInput: false,
});

/* -------- Toggle --------*/

export type ToggleProps = FormFieldProps<{
  /**
   * Selection mode - single selection (radio) or multiple selection (checkbox)
   */
  selectionMode?: 'single' | 'multiple';
  /**
   * Whether to show the native checkbox/radio input elements
   */
  showInput?: boolean;
  /**
   * For use with uncontrolled single selection state management.
   */
  defaultValue?: string;
  /**
   * For use with controlled single selection state management.
   */
  selectedValue?: string;
  /**
   * For use with controlled single selection state management. An event which receives the value of the selected ToggleItem.
   */
  onSelectionChange?: (value: string) => void;
  /**
   * For use with uncontrolled multiple selection state management.
   */
  defaultValues?: string[];
  /**
   * For use with controlled multiple selection state management.
   */
  selectedValues?: string[];
  /**
   * For use with controlled multiple selection state management. An event which receives the array of selected ToggleItem values.
   */
  onMultiSelectionChange?: (values: string[]) => void;
  /**
   * Passed to the `name` attribute on the underlying radio inputs which associates them together as a group. A randomly generated ID will be used if nothing is passed.
   */
  name?: string;
  size?: 'small' | 'medium' | 'large';
  layout: FormFieldGroupProps['layout'];
  disabled?: boolean;
}>;

export const ToggleConfig = createViewConfig({
  name: 'Toggle',
  props: {} as ToggleProps,
  defaults: {
    size: 'medium',
    layout: 'horizontal',
    disabled: false,
    selectionMode: 'single',
    showInput: false,
  },
});

export const Toggle = ToggleConfig.createView(
  (props: View.RenderProps<ToggleProps>) => {
    const isMultiSelect = props.selectionMode === 'multiple';
    const [fieldProps, controlProps] = useFormFieldProps(props, {
      valueProp: isMultiSelect ? 'selectedValues' : 'selectedValue',
      changeProp: isMultiSelect
        ? 'onMultiSelectionChange'
        : 'onSelectionChange',
    });

    const {
      label,
      hiddenElements,
      ref: fieldRef,
      size: _fieldSize,
      ...restFieldProps
    } = fieldProps;
    const {
      ref: controlRef,
      children,
      selectionMode = 'single',
      showInput = false,
      defaultValue,
      selectedValue,
      onSelectionChange,
      defaultValues,
      selectedValues,
      onMultiSelectionChange,
      size,
      disabled = false,
      layout,
      name,
      ...restProps
    } = controlProps;

    const [selected, setSelected] = useControlledState(
      selectedValue,
      defaultValue,
      onSelectionChange,
    );

    // Multi selection state management
    const [selectedMultiple, setSelectedMultiple] = useControlledState(
      selectedValues,
      defaultValues,
      onMultiSelectionChange,
    );

    const groupId = useId();

    return (
      <ToggleContext.Provider
        value={{
          selectedValue: selected,
          onSelectionChange: setSelected,
          selectionMode,
          selectedValues: selectedMultiple,
          onMultiSelectionChange: setSelectedMultiple,
          size: size || 'medium',
          layout,
          id: groupId,
          name,
          disabled,
          showInput,
        }}
      >
        <FormFieldGroupConfig.Customize
          subviews={{
            inputs: {
              css: [
                size === 'large'
                  ? css({gap: 'space.150'})
                  : css({gap: 'small'}),
                layout === 'horizontal'
                  ? css({
                      alignX: 'start',
                      ':has(div[data-highlighted=true])': {
                        marginTop: '26px',
                      },
                    })
                  : css({alignX: 'stretch'}),
              ],
            },
          }}
        >
          <FormFieldGroup
            legend={label}
            hiddenElements={hiddenElements?.map((element) => {
              if (element === 'label') {
                return 'legend';
              }
              return element;
            })}
            layout={layout}
            ref={controlRef ?? fieldRef}
            {...restFieldProps}
            {...restProps}
          >
            {children}
          </FormFieldGroup>
        </FormFieldGroupConfig.Customize>
      </ToggleContext.Provider>
    );
  },
  {
    css: {
      stack: 'y',
      width: 'fill',
    },
    subviews: {
      inputs: {
        css: {
          marginY: 'xsmall',
          alignY: 'stretch',
          width: 'fill',
        },
      },
    },
    forward: {
      size: true,
    },
  },
);

/* -------- ToggleItem --------*/

export type ToggleItemProps = View.IntrinsicElement<
  'div',
  {
    label: React.ReactNode;
    /**
     * Used as the `value` attribute on the underlying `<input type="radio">` or `<input type="checkbox">` element.
     */
    value: string;
    description?: React.ReactNode;
    disabled?: boolean;
    icon?: IconAsset;
    highlight?: string;
    image?: string;
    subviews?: View.Subviews<{
      icon: typeof Icon;
      label: typeof Label;
      description: 'div';
      input: 'input';
      highlight: 'div';
      children: 'div';
      image: 'img';
    }>;
  }
>;

export const ToggleItemConfig = createViewConfig({
  name: 'ToggleItem',
  props: {} as ToggleItemProps,
  defaults: {
    disabled: false,
  },
});

export const ToggleItem = ToggleItemConfig.createView(
  ({
    label,
    value,
    description,
    disabled,
    children,
    icon,
    subviews,
    highlight,
    image,
    ...rest
  }) => {
    const {
      selectedValue,
      onSelectionChange,
      selectionMode,
      selectedValues,
      onMultiSelectionChange,
      size,
      layout,
      id: groupId,
      disabled: disabledByParent,
      name,
      showInput,
    } = React.useContext(ToggleContext);
    const id = useId();
    const selectorSafeId = id.replace(/[:\u00ab\u00bb]/g, '_');
    const highlightId = `${selectorSafeId}-highlight`;
    const labelId = `${selectorSafeId}-label`;

    // Determine if this item is selected based on selection mode
    const isSelected =
      selectionMode === 'multiple'
        ? selectedValues?.includes(value) || false
        : value === selectedValue;

    const isDisabled = disabledByParent || disabled;

    // Handle selection changes based on mode
    const handleSelectionChange = React.useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (selectionMode === 'multiple') {
          if (!onMultiSelectionChange || !selectedValues) {
            return;
          }

          const newValues = e.target.checked
            ? [...selectedValues, value]
            : selectedValues.filter((v) => v !== value);

          onMultiSelectionChange(newValues);
        } else if (onSelectionChange) {
          onSelectionChange(e.target.value);
        }
      },
      [
        selectionMode,
        onMultiSelectionChange,
        selectedValues,
        value,
        onSelectionChange,
      ],
    );

    return (
      <view.div
        data-checked={isSelected}
        data-disabled={isDisabled}
        data-highlighted={highlight ? true : undefined}
        css={[
          isDisabled
            ? css({backgroundColor: 'offset'})
            : css({backgroundColor: 'surface'}),
          // eslint-disable-next-line no-nested-ternary
          size === 'large'
            ? css({font: 'label.large'})
            : size === 'medium'
            ? css({font: 'label.medium'})
            : css({font: 'label.small'}),
          image ? css({stack: 'y'}) : css({stack: 'x'}),
          horizontalPadding[size],
          verticalPadding[size],
          gap[size],
          highlight && layout === 'vertical'
            ? css({
                // We add a margin to the top of the ToggleItem when one of the items has a highlight and its vertical layout(24px badge height + 2px shadow).
                marginTop: '26px',
              })
            : css({}),
        ]}
        uses={[
          IconConfig.customize({
            defaults: {size: size === 'large' ? 'small' : 'xsmall'},
            css: {pointerEvents: 'none'},
          }),
        ]}
        {...rest}
      >
        {/* Always have the functional hidden input */}
        <view.input
          type={selectionMode === 'multiple' ? 'checkbox' : 'radio'}
          id={id}
          value={value}
          checked={isSelected}
          onChange={handleSelectionChange}
          // Need a name attribute to group radio inputs together, which enables out-of-the-box keyboard interactions
          name={name || groupId}
          disabled={isDisabled}
          aria-labelledby={highlight ? `${highlightId} ${labelId}` : undefined}
          inherits={subviews.input}
        />
        {highlight ? (
          <view.div
            id={highlightId}
            aria-hidden="true"
            data-checked={isSelected}
            data-disabled={isDisabled}
            inherits={subviews.highlight}
          >
            {highlight}
          </view.div>
        ) : null}
        {image ? (
          <view.div
            css={[
              css({
                flex: 1,
                borderRadius: 'action',
              }),
              bleedTop[size],
              bleedX[size],
              isSelected
                ? css({backgroundColor: $['color.primary.25']})
                : css({backgroundColor: 'offset'}),
            ]}
          >
            <view.img alt=" " src={image} inherits={subviews.image} />
          </view.div>
        ) : null}
        {/* Show visual components when showInput is true.
           These inputs are display-only; selection state is managed by the
           parent ToggleItem's onClick handler. `readOnly` tells React that
           setting `checked` without `onChange` is intentional, suppressing
           the "checked prop without onChange" controlled-input warning. */}
        {showInput &&
          (selectionMode === 'multiple' ? (
            <NativeCheckbox
              checked={isSelected}
              disabled={isDisabled}
              readOnly
              tabIndex={-1}
              onClick={() => {
                if (!isDisabled) {
                  const mockEvent = {
                    target: {checked: !isSelected},
                  } as React.ChangeEvent<HTMLInputElement>;
                  handleSelectionChange(mockEvent);
                }
              }}
            />
          ) : (
            <NativeRadio
              checked={isSelected}
              disabled={isDisabled}
              readOnly
              tabIndex={-1}
              onClick={() => {
                if (!isDisabled) {
                  const mockEvent = {
                    target: {value, checked: true},
                  } as React.ChangeEvent<HTMLInputElement>;
                  handleSelectionChange(mockEvent);
                }
              }}
            />
          ))}
        {icon ? (
          <Icon
            icon={icon}
            inherits={subviews.icon}
            /*
              When set to `stack: x`, the height of ToggleItems much match the height of the Button and other form elements in their respective size. This means we can't adjust the padding/font size.

              When set to `stack: y`, the icon in the ToggleBoxItem is too close to the top border. We use a margin here to account for this. This margin is not visible in the horizontal orientation because of our alignment system.
            */
            css={
              size === 'small'
                ? css({marginTop: 'space.0'})
                : css({marginTop: 'xsmall'})
            }
          />
        ) : null}
        <view.div
          css={[
            css({width: 'fill'}),
            highlight ? css({alignSelf: 'start'}) : css({}),
          ]}
        >
          {/* We use a ghost element to ensure the layout is correct when the highlight is present */}
          {highlight ? (
            <view.div
              aria-hidden="true"
              css={{
                font: 'label.small.emphasized',
                visibility: 'hidden',
                height: 'space.0',
                display: 'block',
                // We add an extra 8px to the width to account for the border.
                marginRight: 'small',
              }}
            >
              {highlight}
            </view.div>
          ) : null}
          <Label
            id={labelId}
            htmlFor={id}
            css={[
              css({
                display: 'block',
                cursor: 'pointer',
                color: 'subdued',
              }),
              isSelected ? css({color: 'action.primary'}) : css({}),
              isDisabled ? css({color: 'action.primary.disabled'}) : css({}),
              // eslint-disable-next-line no-nested-ternary
              size === 'large'
                ? css({font: 'label.large.emphasized'})
                : size === 'medium'
                ? css({font: 'label.medium.emphasized'})
                : css({font: 'label.small.emphasized'}),
            ]}
            inherits={subviews.label}
          >
            {label}
          </Label>
          {description ? (
            <view.div
              css={[
                isDisabled
                  ? css({color: 'action.primary.disabled'})
                  : css({color: 'subdued'}),
                size === 'large'
                  ? css({font: 'body.medium'})
                  : css({font: 'body.small'}),
              ]}
              inherits={subviews.description}
            >
              {description}
            </view.div>
          ) : null}

          {children ? (
            <view.div inherits={subviews.children} uses={[actionOverrides]}>
              {children}
            </view.div>
          ) : null}
        </view.div>
      </view.div>
    );
  },
  {
    css: {
      position: 'relative',
      borderRadius: 'action',
      color: 'subdued',
      transition: 'all 0.2s',
      ':hover:not([data-disabled=true])': {
        backgroundColor: 'offset',
      },
      ':hover[data-checked=true]:not([data-disabled=true])': {
        backgroundColor: $['color.primary.25'],
      },
      keyline: 'border',
      keylineWidth: 'small',
      '[data-highlighted=true]': {
        position: 'relative',
        height: 'auto',
        borderRadius: `0 ${$['border.radius.action']} ${$['border.radius.action']} ${$['border.radius.action']}`,
      },
      '[data-checked=true]': {
        keyline: 'action.primary',
        keylineWidth: 'medium',
      },
      '[data-checked=true]:not([data-disabled=true])': {
        color: 'action.primary',
      },
      '[data-disabled=true]': {
        color: 'action.primary.disabled',
      },
    },
    subviews: {
      description: {
        uses: [actionOverrides],
      },
      image: {
        css: {
          width: '100%',
          height: 'auto',
          display: 'block',
          borderRadius: 'action',
        },
      },
      input: {
        css: {
          appearance: 'none',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          margin: 'space.0',
          width: 'fill',
          height: 'fill',
          cursor: 'pointer',
          borderRadius: 'action',
          ':focus': {
            focusRing: 'focus',
          },
          ':disabled': {
            cursor: 'default',
          },
        },
      },
      highlight: {
        css: {
          position: 'absolute',
          bottom: 'calc(100% + 2px)',
          left: 0,
          color: 'text',
          backgroundColor: $['color.border'],
          keyline: 'border',
          borderRadius: `${$['border.radius.action']} ${$['border.radius.action']} 0 0`,
          font: 'label.small.emphasized',
          paddingX: 'xsmall',
          paddingTop: 'xxsmall',
          paddingBottom: 'xsmall',
          transition: 'all 0.2s',
          '[data-checked=true]': {
            keylineWidth: 2,
          },
          '[data-checked=true]:not([data-disabled=true])': {
            color: 'onAction.primary',
            backgroundColor: 'action.primary',
            keyline: 'action.primary',
          },
          '[data-disabled=true]': {
            color: 'action.primary.disabled',
            backgroundColor: $['color.border'],
            keyline: 'border',
          },
          '::after': {
            content: '""',
            display: 'block',
            borderBottomLeftRadius: '2px',
            height: 'fill',
            width: '2px',
            position: 'absolute',
            background: 'none',
          },
          ':not([data-checked=true])::after': {
            boxShadow: `0 1px 0 0 ${$['color.border']}`,
            left: 'calc(100% + 1px)',
            top: '1px',
          },
          '[data-checked=true]::after': {
            boxShadow: `0 2px 0 0 ${$['color.background.action.primary']}`,
            left: 'calc(100% + 2px)',
            top: '0',
          },
        },
      },
    },
  },
);
